/**
 * RMDLedger — Distribution ledger with auto-classification from custodian transactions.
 *
 * QuickBooks-style reconciliation: auto-classifies transactions, advisor reviews & accepts.
 */

import { useContext, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  authenticatedFetch,
  useAuthenticatedFetch,
  useAuthenticatedMutationNew,
} from "../../lib/api";
import { formatCurrency } from "../../lib/numbers";
import { NotificationContext } from "../../Notifications";
import type {
  RMDLedgerEntry,
  RMDLedgerSummary,
  RMDLedgerClassification,
  RMDLedgerStatus,
  RMDDistribution,
  UpdateRMDLedgerEntry,
} from "../../../../api/src/rmd/rmd-lib";
import type { WrappedApiResponse } from "../../../../api/src/lib";

type LedgerResponse = {
  entries: RMDLedgerEntry[];
  summary: RMDLedgerSummary;
};

const CLASSIFICATION_OPTIONS: {
  value: RMDLedgerClassification;
  label: string;
  variant: string;
}[] = [
  { value: "distribution", label: "Distribution", variant: "primary" },
  { value: "qcd", label: "QCD", variant: "info" },
  { value: "fed_withholding", label: "Fed W/H", variant: "warning" },
  { value: "state_withholding", label: "State W/H", variant: "warning" },
  { value: "returned_check", label: "Returned", variant: "danger" },
  { value: "excluded", label: "Excluded", variant: "secondary" },
];

function classificationLabel(c: RMDLedgerClassification): string {
  return CLASSIFICATION_OPTIONS.find((o) => o.value === c)?.label ?? c;
}

function statusVariant(s: RMDLedgerStatus): string {
  return s === "accepted"
    ? "success"
    : s === "rejected"
      ? "secondary"
      : "warning";
}

function fmt(n: number): string {
  return formatCurrency(Math.abs(n), 2);
}

export type DuplicateMatch = {
  manualDate: string;
  manualAmount: number;
  ledgerDate: string;
  ledgerAmount: number;
  ledgerDescription: string;
};

/**
 * Detect potential duplicates between manual distributions and accepted ledger entries.
 * Heuristic: |date difference| <= 1 day AND |amount difference| < $1.00
 */
export function detectDuplicates(
  manualDistributions: RMDDistribution[],
  ledgerEntries: RMDLedgerEntry[],
): DuplicateMatch[] {
  const accepted = ledgerEntries.filter((e) => e.status === "accepted");
  const matches: DuplicateMatch[] = [];

  for (const manual of manualDistributions) {
    const manualDate = new Date(manual.distributionDate).getTime();
    const manualAmt = manual.grossAmount;

    for (const entry of accepted) {
      const entryDate = new Date(entry.transactionDate).getTime();
      const entryAmt = Math.abs(entry.amount);
      const daysDiff = Math.abs(manualDate - entryDate) / 86_400_000;
      const amtDiff = Math.abs(manualAmt - entryAmt);

      if (
        daysDiff <= 1 &&
        amtDiff < 1 &&
        entry.classification !== "fed_withholding" &&
        entry.classification !== "state_withholding" &&
        entry.classification !== "returned_check" &&
        entry.classification !== "excluded"
      ) {
        matches.push({
          manualDate: new Date(manual.distributionDate).toLocaleDateString(
            undefined,
            { timeZone: "UTC" },
          ),
          manualAmount: manualAmt,
          ledgerDate: new Date(entry.transactionDate).toLocaleDateString(
            undefined,
            { timeZone: "UTC" },
          ),
          ledgerAmount: entryAmt,
          ledgerDescription: entry.description,
        });
      }
    }
  }
  return matches;
}

export const DuplicateWarningBanner = ({
  duplicates,
}: {
  duplicates: DuplicateMatch[];
}) => {
  if (duplicates.length === 0) return null;
  return (
    <Alert variant="warning" className="mb-3">
      <strong>Potential duplicate:</strong>{" "}
      {duplicates.map((d, i) => (
        <span key={i}>
          {i > 0 && "; "}a {fmt(d.manualAmount)} distribution on {d.manualDate}{" "}
          appears in both manual logs and the transaction ledger
        </span>
      ))}
      . Resolve by deleting the manual entry or rejecting the ledger entry.
    </Alert>
  );
};

const InlineNotes = ({
  value,
  placeholder,
  onSave,
}: {
  value: string;
  placeholder?: string;
  onSave: (notes: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  const save = () => {
    setEditing(false);
    if (text !== value) onSave(text);
  };

  if (editing) {
    return (
      <Form.Control
        size="sm"
        type="text"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") {
            setText(value);
            setEditing(false);
          }
        }}
        autoFocus
        style={{ minWidth: 120 }}
      />
    );
  }

  return (
    <span
      className={`small ${value ? "" : "text-muted"}`}
      style={{ cursor: "pointer" }}
      onClick={() => setEditing(true)}
      title="Click to add notes"
    >
      {value || placeholder || "—"}
    </span>
  );
};

const RMDLedger = ({
  householdId,
  profileId,
  annualId,
  requiredAmount,
  manualDistributions,
}: {
  householdId: number | string;
  profileId: number;
  annualId: number;
  requiredAmount: number;
  manualDistributions: RMDDistribution[];
}) => {
  const { pushNotification } = useContext(NotificationContext);
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<RMDLedgerStatus | "">("");

  // `GET /v1/.../ledger` (APP-1653).
  const ledgerBaseUrl = `/v1/households/${householdId}/rmd`;

  // Fetch ledger — the annualId in the query key triggers refetch on change
  const ledgerUrl = `${ledgerBaseUrl}/profiles/${profileId}/annuals/${annualId}/ledger${
    statusFilter ? `?status=${statusFilter}` : ""
  }`;
  const { data: ledgerResponse, isPending } =
    useAuthenticatedFetch<WrappedApiResponse<LedgerResponse>>(ledgerUrl);
  const entries = ledgerResponse?.data?.entries ?? [];
  const summary = ledgerResponse?.data?.summary;

  const invalidateLedger = () => {
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        q.queryKey.some((k) => typeof k === "string" && k.includes("ledger")),
    });
    // Also invalidate annuals since totals may have changed
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        q.queryKey.some((k) => typeof k === "string" && k.includes("annuals")),
    });
  };

  // Mutations
  // `POST .../ledger`, which was `POST .../ledger/refresh` (APP-1653; row 7
  // of an-decision-01a0ef4b7e9377c9).
  const refreshMut = useAuthenticatedMutationNew<
    WrappedApiResponse<LedgerResponse>,
    unknown,
    Record<string, never>
  >({
    mutationKey: [
      "POST",
      "households",
      String(householdId),
      "rmd",
      "profiles",
      String(profileId),
      "annuals",
      String(annualId),
      "ledger",
    ],
    // Under `/v1` (APP-1653). The key is unchanged.
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () => {
        invalidateLedger();
        pushNotification({
          id: "ledger-refresh",
          header: "Refreshed",
          body: "Transactions refreshed",
          variant: "success",
        });
      },
    },
  });

  const acceptAllMut = useAuthenticatedMutationNew<
    WrappedApiResponse<LedgerResponse>,
    unknown,
    { minConfidence?: string }
  >({
    mutationKey: [
      "POST",
      "households",
      String(householdId),
      "rmd",
      "profiles",
      String(profileId),
      "annuals",
      String(annualId),
      "ledger",
      "accept-all",
    ],
    // Not under `/v1`: `POST .../ledger/accept-all` stays unversioned, its
    // path not approved (`docs/adr/client-domain-admission.md`, APP-1653).
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () => {
        invalidateLedger();
        pushNotification({
          id: "ledger-accept-all",
          header: "Accepted",
          body: "High-confidence entries accepted",
          variant: "success",
        });
      },
    },
  });

  // `POST .../ledger/accepted`, which was `POST .../ledger/accept-suggested`
  // (APP-1653; row 7 of an-decision-01a0ef4b7e9377c9).
  const acceptSuggestedMut = useAuthenticatedMutationNew<
    WrappedApiResponse<LedgerResponse>,
    unknown,
    { entryIds: number[] }
  >({
    mutationKey: [
      "POST",
      "households",
      String(householdId),
      "rmd",
      "profiles",
      String(profileId),
      "annuals",
      String(annualId),
      "ledger",
      "accepted",
    ],
    // Under `/v1` (APP-1653). The key is unchanged.
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () => invalidateLedger(),
    },
  });

  const updateEntry = useCallback(
    async (entryId: number, body: UpdateRMDLedgerEntry) => {
      // `PUT /v1/.../rmd/ledger/:entryId` (APP-1653).
      const url = `${process.env.REACT_APP_API_ROOT}/v1/households/${householdId}/rmd/ledger/${entryId}`;
      await authenticatedFetch(url, getAccessTokenSilently, {
        method: "PUT",
        body,
      })();
      invalidateLedger();
    },
    [householdId, getAccessTokenSilently],
  );

  const handleAccept = (entry: RMDLedgerEntry) => {
    acceptSuggestedMut.mutate({ entryIds: [entry.id] } as any);
  };

  const handleReject = (entry: RMDLedgerEntry) => {
    updateEntry(entry.id, { status: "rejected" });
  };

  const handleReclassify = (
    entry: RMDLedgerEntry,
    newClassification: RMDLedgerClassification,
  ) => {
    updateEntry(entry.id, {
      classification: newClassification,
      status: "accepted",
    });
  };

  const handleUndo = (entry: RMDLedgerEntry) => {
    updateEntry(entry.id, {
      status: "pending",
      classification: entry.suggestedClassification,
    });
  };

  const pendingCount = summary?.pendingCount ?? 0;
  // Align with the household profile's Remaining RMD stat: include both
  // manual distributions and accepted ledger entries. The ledger summary
  // only covers ledger entries, so add manual distributions separately.
  const manualDistributed = manualDistributions.reduce(
    (sum, d) => sum + d.grossAmount,
    0,
  );
  const remaining =
    requiredAmount - manualDistributed - (summary?.totalDistributed ?? 0);
  const duplicates = detectDuplicates(manualDistributions, entries);

  if (isPending) return <p className="text-muted">Loading ledger...</p>;

  return (
    <Card>
      <Card.Body>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            Distribution Ledger &mdash; {new Date().getFullYear()}
          </h6>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => refreshMut.mutate({} as any)}
              disabled={refreshMut.isPending}
            >
              Refresh Transactions
            </Button>
            {pendingCount > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  acceptAllMut.mutate({ minConfidence: "high" } as any)
                }
                disabled={acceptAllMut.isPending}
              >
                Accept All Suggested
              </Button>
            )}
          </div>
        </div>

        {/* Duplicate Warning */}
        <DuplicateWarningBanner duplicates={duplicates} />

        {/* Summary Bar */}
        {summary && (
          <>
            <Row className="g-2 mb-3">
              <Col>
                <div className="border rounded p-2 text-center">
                  <div className="text-muted small">Total Distributed</div>
                  <div className="fw-bold">{fmt(summary.totalDistributed)}</div>
                </div>
              </Col>
              <Col>
                <div className="border rounded p-2 text-center">
                  <div className="text-muted small">Taxable</div>
                  <div className="fw-bold">
                    {fmt(summary.taxableDistributions)}
                  </div>
                </div>
              </Col>
              <Col>
                <div className="border rounded p-2 text-center">
                  <div className="text-muted small">QCD</div>
                  <div className="fw-bold">{fmt(summary.qcdTotal)}</div>
                </div>
              </Col>
              <Col>
                <div className="border rounded p-2 text-center">
                  <div className="text-muted small">Fed W/H</div>
                  <div className="fw-bold">
                    {fmt(summary.fedWithholdingTotal)}
                  </div>
                </div>
              </Col>
              <Col>
                <div className="border rounded p-2 text-center">
                  <div className="text-muted small">State W/H</div>
                  <div className="fw-bold">
                    {fmt(summary.stateWithholdingTotal)}
                  </div>
                </div>
              </Col>
            </Row>
            <div className="d-flex justify-content-between mb-3 text-muted small">
              <span>
                Remaining RMD:{" "}
                <strong
                  className={remaining > 0 ? "text-danger" : "text-success"}
                >
                  {fmt(remaining)}
                </strong>
              </span>
              {pendingCount > 0 && (
                <span>
                  <Badge bg="warning" text="dark">
                    {pendingCount}
                  </Badge>{" "}
                  pending review
                </span>
              )}
            </div>
          </>
        )}

        {/* Filter */}
        <div className="mb-3">
          <Form.Select
            size="sm"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as RMDLedgerStatus | "")
            }
            style={{ width: "auto", display: "inline-block" }}
          >
            <option value="">All entries</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </Form.Select>
        </div>

        {/* Ledger Table */}
        {entries.length === 0 ? (
          <p className="text-muted text-center my-4">
            No transactions found for this account and year.
          </p>
        ) : (
          <Table size="sm" responsive hover>
            <thead>
              <tr>
                <th>Date</th>
                <th className="text-end">Amount</th>
                <th>Description</th>
                <th>Code</th>
                <th>Classification</th>
                <th>Notes</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className={entry.status === "rejected" ? "text-muted" : ""}
                  style={
                    entry.status === "rejected" ? { opacity: 0.5 } : undefined
                  }
                >
                  <td>
                    {new Date(entry.transactionDate).toLocaleDateString(
                      undefined,
                      { timeZone: "UTC" },
                    )}
                  </td>
                  <td className="text-end">
                    <span
                      className={entry.amount > 0 ? "text-success" : undefined}
                    >
                      {entry.amount > 0 ? "+" : ""}
                      {formatCurrency(entry.amount, 2)}
                    </span>
                  </td>
                  <td>
                    <OverlayTrigger
                      overlay={<Tooltip>{entry.description}</Tooltip>}
                    >
                      <span
                        style={{
                          maxWidth: 250,
                          display: "inline-block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.description}
                      </span>
                    </OverlayTrigger>
                  </td>
                  <td>
                    <code className="small">{entry.sourceTransactionCode}</code>
                  </td>
                  <td>
                    {entry.status === "rejected" ? (
                      <Badge bg="secondary">
                        {classificationLabel(entry.classification)}
                      </Badge>
                    ) : (
                      <Form.Select
                        size="sm"
                        value={entry.classification}
                        onChange={(e) =>
                          handleReclassify(
                            entry,
                            e.target.value as RMDLedgerClassification,
                          )
                        }
                        style={{ width: "auto", minWidth: 120 }}
                      >
                        {CLASSIFICATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </td>
                  <td>
                    <InlineNotes
                      value={entry.notes ?? ""}
                      placeholder={
                        entry.classification === "qcd" ? "Charity name..." : ""
                      }
                      onSave={(notes) =>
                        updateEntry(entry.id, { notes: notes || null })
                      }
                    />
                  </td>
                  <td>
                    <span className="d-inline-flex gap-1 align-items-center">
                      <Badge bg={statusVariant(entry.status)}>
                        {entry.status}
                      </Badge>
                      {entry.status === "pending" &&
                        entry.confidence !== "high" && (
                          <Badge bg="light" text="dark">
                            {entry.confidence}
                          </Badge>
                        )}
                    </span>
                  </td>
                  <td>
                    {entry.status === "pending" && (
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleAccept(entry)}
                          title="Accept"
                        >
                          &#x2713;
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleReject(entry)}
                          title="Reject"
                        >
                          &#x2717;
                        </Button>
                      </div>
                    )}
                    {entry.status === "accepted" && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleUndo(entry)}
                        title="Undo"
                      >
                        &#x21A9;
                      </Button>
                    )}
                    {entry.status === "rejected" && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleUndo(entry)}
                        title="Restore"
                      >
                        &#x21A9;
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default RMDLedger;
